import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { get } from "../../api/axios"
import { firstDateValue, formatDate, parseDate } from "../../helpers/formatDate"
import endpoints from "../../api/endpoints"
import { ICONS } from "../../constants/icons"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { formatDurationFromHours } from "../../helpers/formatDuration"
import {
  getUserFullName,
  isSameUserId,
  nameWithYou,
} from "../../helpers/userDisplay"
import ProjectDetailsCard from "./overview/ProjectDetailsCard"
import HoursCard from "./overview/HoursCard"
import EstimatedHoursCard from "./overview/EstimatedHoursCard"
import CollaboratorsCard from "./overview/CollaboratorsCard"
import CountdownCard from "./overview/CountdownCard"
import SessionsLogCard from "./overview/SessionsLogCard"
import MediaCard from "./overview/MediaCard"

const OVERVIEW_LIST_LIMIT = 8
const ESTIMATED_PAGE_SIZE = 5

/** Inclusive local calendar days between project start/end (for week↔day conversion). */
const getProjectInclusiveDayCount = project => {
  const startRaw = firstDateValue(project?.startDate, project?.start_date)
  const endRaw = firstDateValue(project?.endDate, project?.end_date)
  if (!startRaw) return null

  const parseLocalDate = value => {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) {
      const fallback = parseDate(value)
      return fallback && !Number.isNaN(fallback.getTime()) ? fallback : null
    }
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  }

  const start = parseLocalDate(startRaw)
  if (!start) return null

  // Ongoing / no end → full week for rate conversion.
  if (!endRaw) return 7

  const end = parseLocalDate(endRaw)
  if (!end) return 7

  const msPerDay = 1000 * 60 * 60 * 24
  const daysDiff = Math.round((end.getTime() - start.getTime()) / msPerDay)
  return Math.max(1, daysDiff + 1)
}

const mergeProjectStats = (project, stats) => {
  if (!project || !stats) return project

  const estimated = Number(stats.total_estimated_hours)
  const logged = Number(stats.total_logged_hours)
  const remaining = Number(stats.remaining_hours)
  const progress = Number(stats.progress_percentage)
  const remainingPct = Number(stats.remaining_percentage)
  const daysLeft =
    stats.days_left == null || stats.days_left === ""
      ? null
      : Number(stats.days_left)

  return {
    ...project,
    hours: {
      ...(project.hours || {}),
      estimatedTotal: Number.isFinite(estimated)
        ? estimated
        : project.hours?.estimatedTotal ?? 0,
      logged: Number.isFinite(logged) ? logged : project.hours?.logged ?? 0,
      remaining: Number.isFinite(remaining)
        ? remaining
        : project.hours?.remaining ?? 0,
      consumed: Number.isFinite(logged) ? logged : project.hours?.consumed ?? 0,
      dedicated: Number.isFinite(logged)
        ? logged
        : project.hours?.dedicated ?? 0,
    },
    progress: Number.isFinite(progress) ? progress : project.progress ?? 0,
    remainingPercentage: Number.isFinite(remainingPct)
      ? remainingPct
      : project.remainingPercentage,
    daysLeft: Number.isFinite(daysLeft) ? daysLeft : project.daysLeft ?? null,
    isOngoing: Boolean(stats.is_ongoing),
    isOverdue: Boolean(stats.is_overdue),
  }
}

export { mergeProjectStats }

const mapOverviewCollaborator = (invitation, currentUserId) => {
  const user = invitation.user || {}
  const roleType = invitation.role_types?.[0] || invitation.role_type || {}
  const rawRole = roleType.public_name || roleType.name || ""
  const userId = user.id || invitation.user_id || null
  const name =
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
    user.full_name ||
    user.name ||
    user.email ||
    ""

  return {
    id: invitation.id || user.id,
    userId,
    name: name
      ? nameWithYou(name, isSameUserId(userId, currentUserId))
      : "",
    role: rawRole ? String(rawRole).trim() : "",
    avatar: getUserAvatarUrl(user),
    status: invitation.status || "active",
  }
}

/** Estimated-hours rows from project-invitations (includes hours_cadence). */
const mapEstimatedHoursItem = (invitation, currentUserId) => {
  const user = invitation.user || {}
  const userId = user.id || invitation.user_id || null
  const name = getUserFullName(user)

  return {
    id: invitation.id || user.id,
    user,
    name: name
      ? nameWithYou(name, isSameUserId(userId, currentUserId))
      : "",
    estimated_hours: Number(invitation.estimated_hours) || 0,
    hours_cadence: invitation.hours_cadence || "per_week",
    progress: 0,
  }
}

/** Future (upcoming) — Figma 40004269-7580
 * Countdown on top, then 2×2:
 * Details | Hours stack
 * Collaborators | Estimated Hours
 */
const UpcomingOverview = ({
  project,
  collaborators,
  estimatedHours,
  isLoadingCollaborators,
  estimatedHasMore,
  isLoadingMoreEstimates,
  onLoadMoreEstimates,
  daysPerWeek,
}) => (
  <div className="project-overview">
    <div className="project-overview__row project-overview__row--full">
      <CountdownCard
        startDate={firstDateValue(project.startDate, project.start_date)}
        duration={{
          start: formatDate(
            firstDateValue(project.startDate, project.start_date)
          ),
          end: parseDate(firstDateValue(project.endDate, project.end_date))
            ? formatDate(firstDateValue(project.endDate, project.end_date))
            : "Ongoing",
        }}
        projectName={project.name}
      />
    </div>

    <div className="project-overview__quad">
      <div className="project-overview__quad-details">
        <ProjectDetailsCard project={project} />
      </div>
      <div className="project-overview__quad-hours project-overview__stack">
        <HoursCard
          title="Total Consumed Hours"
          icon={ICONS.timer}
          value={formatDurationFromHours(project.hours?.consumed)}
          tone="orange-custom"
          layout="inline"
          fullHeight
        />
        <HoursCard
          title="Hours Per Week"
          icon={ICONS.hourglass}
          value={`${project.hours?.perWeek ?? 0}h/week`}
          tone="red"
          layout="inline"
          fullHeight
        />
      </div>
      <div className="project-overview__quad-collaborators">
        <CollaboratorsCard
          collaborators={collaborators}
          isLoading={isLoadingCollaborators}
          fullHeight
        />
      </div>
      <div className="project-overview__quad-side">
        <EstimatedHoursCard
          list={estimatedHours}
          total={project.hours?.estimatedTotal ?? 0}
          fullHeight
          hasMore={estimatedHasMore}
          isLoadingMore={isLoadingMoreEstimates}
          onLoadMore={onLoadMoreEstimates}
          daysPerWeek={daysPerWeek}
        />
      </div>
    </div>
  </div>
)

/**
 * Current (in-progress) — Figma 40004144-1796:
 * Top: Details | Estimated Hours | Logged + Remaining
 * Bottom: Sessions Logs | Collaborators
 */
const InProgressOverview = ({
  project,
  collaborators,
  estimatedHours,
  isLoadingCollaborators,
  estimatedHasMore,
  isLoadingMoreEstimates,
  onLoadMoreEstimates,
  daysPerWeek,
}) => (
  <div className="project-overview">
    <div className="project-overview__row project-overview__row--triple">
      <ProjectDetailsCard project={project} />
      <EstimatedHoursCard
        list={estimatedHours}
        total={project.hours?.estimatedTotal ?? 0}
        showProgress
        fullHeight
        tone="lightteal"
        hasMore={estimatedHasMore}
        isLoadingMore={isLoadingMoreEstimates}
        onLoadMore={onLoadMoreEstimates}
        daysPerWeek={daysPerWeek}
      />
      <div className="project-overview__stack">
        <HoursCard
          title="Total Logged Hours"
          icon={ICONS.timer}
          value={formatDurationFromHours(project.hours?.logged)}
          tone="orange-custom"
          layout="inline"
          fullHeight
        />
        <HoursCard
          title="Total Remaining Hours"
          icon={ICONS.hourglass}
          value={formatDurationFromHours(project.hours?.remaining)}
          tone="red"
          layout="inline"
          fullHeight
        />
      </div>
    </div>

    <div className="project-overview__row project-overview__row--sessions">
      <SessionsLogCard
        fullHeight
        sessions={(project.sessions || []).slice(0, 5)}
      />
      <CollaboratorsCard
        fullHeight
        showStatus
        showViewAll
        collaborators={collaborators}
        isLoading={isLoadingCollaborators}
      />
    </div>
  </div>
)

/** Past — Figma 40004311-5379
 * 2×2 grid: Details | Hours stack
 *           Collaborators | Media (same column widths)
 */
const PastOverview = ({ project, collaborators, isLoadingCollaborators }) => (
  <div className="project-overview">
    <div className="project-overview__quad">
      <div className="project-overview__quad-details">
        <ProjectDetailsCard project={project} />
      </div>
      <div className="project-overview__quad-hours project-overview__stack">
        <HoursCard
          title="Total Dedicated Hours"
          icon={ICONS.timer}
          value={formatDurationFromHours(project.hours?.dedicated)}
          tone="orange-custom"
          layout="inline"
          fullHeight
        />
        <HoursCard
          title="Hours Per Week"
          icon={ICONS.hourglass}
          value={`${project.hours?.perWeek ?? 0}h/week`}
          tone="red"
          layout="inline"
          fullHeight
        />
      </div>
      <div className="project-overview__quad-collaborators">
        <CollaboratorsCard
          fullHeight
          collaborators={collaborators}
          isLoading={isLoadingCollaborators}
        />
      </div>
      <div className="project-overview__quad-side">
        <MediaCard fullHeight projectId={project?.id} />
      </div>
    </div>
  </div>
)

/**
 * ProjectOverview
 * ------------------------------------------------------------------
 * Overview tab for a project — Future / Current / Past layouts from
 * Figma, composed from shared Dashboard + Custom* components.
 */
const ProjectOverview = ({ project }) => {
  const projectId = project?.id
  const currentUserId = useSelector(state => state.auth?.currentUser?.id)
  const currentUserType = useSelector(state => state.auth?.currentUser?.type)
  const [collaborators, setCollaborators] = useState([])
  const [estimatedHours, setEstimatedHours] = useState([])
  const [estimatedPage, setEstimatedPage] = useState(1)
  const [estimatedHasMore, setEstimatedHasMore] = useState(false)
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(true)
  const [isLoadingMoreEstimates, setIsLoadingMoreEstimates] = useState(false)
  const [stats, setStats] = useState(null)

  const involvementParams =
    currentUserType === "member"
      ? { involvement: "all" }
      : { involvement: "admin" }

  const mapInvitations = invitations => {
    const mappedCollaborators = invitations
      .map(item => mapOverviewCollaborator(item, currentUserId))
      .filter(person => person.name)
    const mappedEstimates = invitations
      .map(item => mapEstimatedHoursItem(item, currentUserId))
      .filter(item => item.name)
    return { mappedCollaborators, mappedEstimates }
  }

  const resolveHasMore = (result, page, pageSize, fetchedCount) => {
    const total = Number(result?.total)
    if (Number.isFinite(total) && total >= 0) {
      return page * pageSize < total
    }
    const lastPage = Number(result?.last_page)
    if (Number.isFinite(lastPage) && lastPage > 0) {
      return page < lastPage
    }
    return fetchedCount >= pageSize
  }

  useEffect(() => {
    let cancelled = false

    const fetchOverviewData = async () => {
      if (!projectId) {
        setCollaborators([])
        setEstimatedHours([])
        setEstimatedPage(1)
        setEstimatedHasMore(false)
        setStats(null)
        setIsLoadingCollaborators(false)
        return
      }

      setIsLoadingCollaborators(true)
      setEstimatedPage(1)
      setEstimatedHasMore(false)

      const fetchFirstPage = async () => {
        try {
          const response = await get(endpoints.getProjectInvitations, {
            params: {
              ...involvementParams,
              project_id: projectId,
              status: "active",
              sort_by: "created_at",
              sort_direction: "desc",
              "with[]": ["user", "roleTypes"],
              page: 1,
              page_size: ESTIMATED_PAGE_SIZE,
              per_page: ESTIMATED_PAGE_SIZE,
            },
          })

          if (cancelled) return

          if (response?.data?.success) {
            const result = response.data.result || {}
            const invitations = result.data || []
            const { mappedCollaborators, mappedEstimates } =
              mapInvitations(invitations)

            setCollaborators(mappedCollaborators.slice(0, OVERVIEW_LIST_LIMIT))
            setEstimatedHours(mappedEstimates)
            setEstimatedPage(1)
            setEstimatedHasMore(
              resolveHasMore(
                result,
                1,
                ESTIMATED_PAGE_SIZE,
                invitations.length,
              ),
            )
          } else {
            setCollaborators([])
            setEstimatedHours([])
            setEstimatedHasMore(false)
          }
        } catch {
          if (!cancelled) {
            setCollaborators([])
            setEstimatedHours([])
            setEstimatedHasMore(false)
          }
        } finally {
          if (!cancelled) setIsLoadingCollaborators(false)
        }
      }

      const fetchStats = async () => {
        try {
          const response = await get(endpoints.getProjectStats(projectId), {
            skipErrorToast: true,
          })
          if (cancelled) return
          if (response?.data?.success && response.data.result) {
            setStats(response.data.result)
          } else {
            setStats(null)
          }
        } catch {
          if (!cancelled) setStats(null)
        }
      }

      await Promise.all([fetchFirstPage(), fetchStats()])
    }

    fetchOverviewData()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount on project/user
  }, [projectId, currentUserId, currentUserType])

  const handleLoadMoreEstimates = async () => {
    if (!projectId || isLoadingMoreEstimates || !estimatedHasMore) return

    const nextPage = estimatedPage + 1
    setIsLoadingMoreEstimates(true)
    try {
      const response = await get(endpoints.getProjectInvitations, {
        params: {
          ...involvementParams,
          project_id: projectId,
          status: "active",
          sort_by: "created_at",
          sort_direction: "desc",
          "with[]": ["user", "roleTypes"],
          page: nextPage,
          page_size: ESTIMATED_PAGE_SIZE,
          per_page: ESTIMATED_PAGE_SIZE,
        },
        skipErrorToast: true,
      })

      if (!response?.data?.success) {
        setEstimatedHasMore(false)
        return
      }

      const result = response.data.result || {}
      const invitations = result.data || []
      const { mappedEstimates } = mapInvitations(invitations)

      setEstimatedHours(prev => {
        const seen = new Set(prev.map(item => String(item.id)))
        const appended = mappedEstimates.filter(
          item => !seen.has(String(item.id)),
        )
        return [...prev, ...appended]
      })
      setEstimatedPage(nextPage)
      setEstimatedHasMore(
        resolveHasMore(result, nextPage, ESTIMATED_PAGE_SIZE, invitations.length),
      )
    } catch {
      setEstimatedHasMore(false)
    } finally {
      setIsLoadingMoreEstimates(false)
    }
  }

  const displayProject = useMemo(
    () => mergeProjectStats(project, stats),
    [project, stats],
  )

  const daysPerWeek = useMemo(() => {
    const dayCount = getProjectInclusiveDayCount(displayProject)
    if (!dayCount) return 7
    return Math.min(7, dayCount)
  }, [displayProject])

  const overviewProps = {
    project: displayProject,
    collaborators: collaborators.slice(0, OVERVIEW_LIST_LIMIT),
    estimatedHours,
    isLoadingCollaborators,
    estimatedHasMore,
    isLoadingMoreEstimates,
    onLoadMoreEstimates: handleLoadMoreEstimates,
    daysPerWeek,
  }

  if (project?.status?.status === "upcoming")
    return <UpcomingOverview {...overviewProps} />
  if (project?.status?.status === "in-progress")
    return <InProgressOverview {...overviewProps} />
  if (project?.status?.status === "past")
    return <PastOverview {...overviewProps} />
  return <InProgressOverview {...overviewProps} />
}

export default ProjectOverview
